/**
 * TicketOwnerPicker - searchable dropdown that swaps the customer
 * ("ticket owner") on an agent ticket detail page.
 *
 * Greg's requirement (2026-05-17):
 *   - 1st dropdown in the Properties card, above Status.
 *   - Closed state: shows the current owner's NAME ONLY (no email).
 *   - Open state: search input on top + a scrolling list of customer
 *     rows. Typing filters server-side by name OR email substring.
 *   - Selecting a row commits the change.
 *
 * The dropdown is NOT a native ``<select>`` because we need an input
 * field at the top. It's a custom listbox-style combobox built on a
 * disclosure ``<button>`` + a positioned panel. Outside-click and
 * Escape close it. ArrowUp/ArrowDown walk the result list, Enter
 * picks the focused row.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { useUserSearch } from "@/hooks/useUserSearch";
import type { AgentUserSearchResult } from "@/types/api";

export interface TicketOwnerValue {
  /** The user_id this ticket is currently assigned to as requester.
   * ``null`` for very old tickets that pre-date the requester FK; in
   * practice every modern ticket has one. */
  id: string | null;
  email: string;
  name: string;
}

export interface TicketOwnerPickerProps {
  value: TicketOwnerValue;
  /** Fired with the FULL selected row so the consumer PATCHes all
   * three fields (id + email + name) together to keep the ticket
   * snapshot in sync with the FK. */
  onChange: (next: TicketOwnerValue) => void;
  /** Current ticket context. Passed to the backend so the empty-query
   * "browse" dropdown pre-populates with same-company colleagues of
   * the current requester instead of random global customers. */
  ticketNumber?: number;
  label?: string;
  id?: string;
}

export function TicketOwnerPicker({
  value,
  onChange,
  ticketNumber,
  label,
  id,
}: TicketOwnerPickerProps): JSX.Element {
  const autoId = useId();
  const inputId = id ?? autoId;
  const listId = `${inputId}-list`;

  // Open / closed state for the dropdown panel.
  const [open, setOpen] = useState(false);
  // Raw search input the user is typing. Debounced inside useUserSearch.
  const [query, setQuery] = useState("");
  // Index of the row that ArrowUp/ArrowDown have focused. -1 = none.
  const [activeIdx, setActiveIdx] = useState(-1);

  // Refs for the disclosure button + panel + input. The outside-click
  // handler reads ``panelRef`` to decide whether a click is inside
  // the picker or outside.
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Search hits, gated on the dropdown being open so a closed picker
  // doesn't poll the server on every keystroke elsewhere on the page.
  // ``ticketNumber`` is forwarded so the empty-query "browse" path
  // returns customers at the same company as the current requester
  // instead of an arbitrary slice of the global customer pool.
  const search = useUserSearch(query, { enabled: open, ticketNumber });
  const rows = useMemo<AgentUserSearchResult[]>(
    () => (search.data ?? []),
    [search.data],
  );

  // Reset the active row whenever the result set changes so the
  // keyboard cursor never points at a stale index.
  useEffect(() => {
    setActiveIdx(rows.length > 0 ? 0 : -1);
  }, [rows]);

  // Focus the input the moment the panel opens. The browser doesn't
  // auto-focus a freshly rendered child of a button-click handler,
  // so we do it explicitly. RAF defers until after layout so the
  // input actually exists in the DOM.
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      // Closing the picker resets the query so reopening doesn't
      // show stale results from the previous session.
      setQuery("");
    }
  }, [open]);

  // Outside-click + Escape closes the panel. The handler is mounted
  // only while open so a closed picker doesn't pay event-handler
  // overhead.
  useEffect(() => {
    if (!open) return;
    function onDocClick(event: MouseEvent) {
      const target = event.target as Node | null;
      if (panelRef.current?.contains(target ?? null)) return;
      if (buttonRef.current?.contains(target ?? null)) return;
      setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function pick(row: AgentUserSearchResult) {
    onChange({ id: row.id, email: row.email, name: row.full_name });
    setOpen(false);
    buttonRef.current?.focus();
  }

  function onInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIdx((idx) => (rows.length === 0 ? -1 : (idx + 1) % rows.length));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIdx((idx) =>
        rows.length === 0 ? -1 : (idx - 1 + rows.length) % rows.length,
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      const row = rows[activeIdx];
      if (row) pick(row);
    }
  }

  return (
    <div className="relative">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-neutral-700"
        >
          {label}
        </label>
      )}
      {/* The disclosure button. Visually styled to match the other
          Properties-card Selects (border + chevron) so the row reads
          as one consistent grid of dropdowns. ``role="combobox"`` is
          both semantically correct (this is a select-only combobox
          per WAI-ARIA 1.2 - a focusable trigger that opens a
          listbox popup) AND it makes the trigger match the existing
          ``[data-just-saved="true"] [role="combobox"]`` CSS selector
          in ``index.css``, so the green-glow save pulse fires on the
          picker the same way it does on the Status / Priority /
          Type ``<select>`` controls next to it. */}
      <button
        ref={buttonRef}
        id={inputId}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
        className="mt-1 flex w-full items-center justify-between rounded-md border border-neutral-300 bg-white px-3 py-2 text-left text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        data-testid="ticket-owner-picker-trigger"
      >
        <span className="truncate" title={value.email}>
          {value.name || value.email || "(no owner)"}
        </span>
        <i
          className="fa-duotone fa-thin fa-chevron-down ml-2 text-neutral-400"
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          ref={panelRef}
          className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-neutral-200 bg-white shadow-lg"
          data-testid="ticket-owner-picker-panel"
        >
          <div className="border-b border-neutral-100 p-2">
            <input
              ref={inputRef}
              type="search"
              placeholder="Search by name or email..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onInputKeyDown}
              aria-controls={listId}
              aria-autocomplete="list"
              className="block w-full rounded border border-neutral-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              data-testid="ticket-owner-picker-input"
            />
          </div>
          <ul
            id={listId}
            role="listbox"
            className="max-h-64 overflow-y-auto py-1"
          >
            {search.isLoading && (
              <li className="px-3 py-2 text-xs text-neutral-500">
                Searching...
              </li>
            )}
            {!search.isLoading && rows.length === 0 && (
              <li className="px-3 py-2 text-xs text-neutral-500">
                No matching customers.
              </li>
            )}
            {rows.map((row, idx) => (
              <li
                key={row.id}
                role="option"
                aria-selected={idx === activeIdx}
                onMouseDown={(e) => {
                  // Use mousedown so the outside-click handler doesn't
                  // close the panel before the click fires.
                  e.preventDefault();
                  pick(row);
                }}
                onMouseEnter={() => setActiveIdx(idx)}
                className={`cursor-pointer px-3 py-2 text-sm ${
                  idx === activeIdx
                    ? "bg-primary-50 text-primary-900"
                    : "text-neutral-800 hover:bg-neutral-50"
                }`}
                data-testid="ticket-owner-picker-option"
              >
                <div className="font-medium">{row.full_name || "(no name)"}</div>
                <div className="text-xs text-neutral-500">{row.email}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default TicketOwnerPicker;
